import React, {useEffect, useState} from 'react'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import Filters from '../Filters/Filters'
import TableCustom, {PaginationType} from 'src/components/elements/TableCustom/TableCustom'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {Snackbar, SxProps, Theme, Typography} from '@mui/material'
import {FormattedMessage} from 'react-intl'
import {STATUS, statesOptions} from 'src/constants/constants'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'

type PatientLookupDialogProps = {
  onClose: () => void;
  onRowClick: (row: Record<string, any>) => void;
  sx?: SxProps<Theme>
};

const PatientLookupDialog: React.FC<PatientLookupDialogProps> = ({onClose, onRowClick, sx}) => {
  const pspPatientsConfigurations = useAppSelector(state => state.pspConfigReducer.data.patientsConfigurations)

  const columns = pspPatientsConfigurations.columns ?? []
  const filterConfig = pspPatientsConfigurations.filters ?? []

  const [filters, setFilters] = useState<Record<string, string | string[]>[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: ''
  })
  const [count, setCount] = useState<number>(0)
  const [statusCount, setStatusCount] = useState<string>()

  useEffect(() => {
    const fetchData = async () => {
      const fetchedData = await apiClient.postPatientsSearch(filters, setStatus, pagination) ?? []
      const parsedData = fetchedData.map((patient: any) => {
        return {
          ...patient,
          state: statesOptions.find((option) => option.value === patient.state)?.label
        }
      })
      setData(parsedData)
      const fetchedDataCount = await apiClient.postPatientsSearchCount(filters, setStatusCount) ?? []
      setCount(fetchedDataCount)
    }

    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination])

  const onSubmit = (values: any) => {
    setFilters(values)
  }

  const body = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'end',
      margin: '8px'
    }}
    >
      <Filters config={filterConfig} formId='patients-filter-form' key='filters' onSubmit={onSubmit} />
      <div style={{marginTop: '16px', width: '100%'}}>
        <TableCustom
          columns={columns}
          onPaginationChange={setPagination}
          onRowClick={onRowClick}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000} onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              <FormattedMessage id='patientsWarning' />
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </div>
  )

  return (
    <DialogCustom
      body={body}
      footer={<></>}
      maxWidth='lg'
      onClose={onClose}
      // The `patientsConfigurations.filters` PSP config can list enough fields
      // (Cognome, Nome, Genere, Data di adesione al programma, Stato) that the
      // Filters popover wraps onto 2 rows. That popover is positioned absolute
      // and doesn't grow this Dialog's Paper, so without a minHeight the Paper
      // ends right after the (short) patients table and the popover's own
      // Cerca/Cancella buttons render past the Paper's bottom edge, appearing
      // to spill outside the dialog frame. Reserving extra height keeps the
      // whole popover inside the visible dialog regardless of field count.
      sx={[sx, {'& .MuiPaper-root': {minHeight: 620}}] as SxProps<Theme>}
      title={{text: 'PatientLookupDialogTitle', format: true}}
    />
  )
}

export default PatientLookupDialog
